import React, { useState } from 'react';
import { Button, Modal, Container, Header, Box } from '@amzn/awsui-components-console';
import ChatWindow from './ChatWindow';
import { sendChatMessage, sendDeviceRecoveryMessage } from './apis';
import './styles/chatbot.scss';

// Default welcome message
const DEFAULT_WELCOME_MESSAGE = {
    id: 'welcome-default',
    type: 'assistant',
    content: `Hi! I'm your Device Farm assistant. I can help you with questions about device checkout, automation, lab management, and more.

What would you like to know about?`,
    timestamp: 0, // Will be set when used
};

// Device recovery welcome message
const DEVICE_RECOVERY_WELCOME_MESSAGE = {
    id: 'welcome-device-recovery',
    type: 'assistant',
    content: `Hello! I'm your Device Recovery Assistant, here to help you get unresponsive or disconnected devices back online.

**How to use me:**
• "recover this [DSN]" 
• "Device [DSN] is stuck, can you help?"

**Friendly reminders:**
• I specialize in device recovery only. For other topics, please use the Default Assistant! 💡 **Important:** Recovering a device you're currently using may interrupt your tests. Please double-check the DSN.

What device can I help you recover today?`,
    timestamp: 0, // Will be set when used
};

// Utility function to generate consistent timestamps
const generateTimestamp = () => {
    return Date.now(); // Return raw timestamp for consistent formatting
};

export default function ChatbotWidget({ className }) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedAssistant, setSelectedAssistant] = useState({ label: 'Default Assistant', value: 'default' });
    const [messages, setMessages] = useState([
        {
            ...DEFAULT_WELCOME_MESSAGE,
            timestamp: generateTimestamp(),
        },
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState('');

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    const handleSendMessage = async (message) => {
        // Prevent sending if already loading
        if (isLoading) return;

        // Add user message
        const userMessage = {
            id: `user-${Date.now()}`,
            type: 'user',
            content: message,
            timestamp: generateTimestamp(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setIsLoading(true);

        try {
            let response;
            let assistantMessage;

            if (selectedAssistant.value === 'device-recovery') {
                // Call device recovery API
                response = await sendDeviceRecoveryMessage(message.trim(), conversationId);

                // Update conversation ID from response if provided
                if (response.conversation_id) {
                    setConversationId(response.conversation_id);
                }

                assistantMessage = {
                    id: `assistant-${Date.now()}`,
                    type: 'assistant',
                    content: response.answer || 'I apologize, but I received an empty response. Please try again.',
                    timestamp: generateTimestamp(),
                };
            } else {
                // Call default LLM service
                response = await sendChatMessage(message.trim(), conversationId);

                // Update conversation ID from response if provided
                if (response.conversation_id) {
                    setConversationId(response.conversation_id);
                }

                assistantMessage = {
                    id: `assistant-${Date.now()}`,
                    type: 'assistant',
                    content:
                        response.answer ||
                        'I apologize, but I received an empty response. Please try rephrasing your question.',
                    timestamp: generateTimestamp(),
                };
            }

            setMessages((prev) => [...prev, assistantMessage]);
        } catch (error) {
            const errorMessage = {
                id: `error-${Date.now()}`,
                type: 'assistant',
                content: error.message || 'I apologize, but I encountered an error. Please try again.',
                timestamp: generateTimestamp(),
            };

            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNewConversation = () => {
        // Reset conversation ID
        setConversationId('');
        // Set appropriate welcome message based on selected assistant
        if (selectedAssistant.value === 'device-recovery') {
            setMessages([
                {
                    ...DEVICE_RECOVERY_WELCOME_MESSAGE,
                    timestamp: generateTimestamp(),
                },
            ]);
        } else {
            setMessages([
                {
                    ...DEFAULT_WELCOME_MESSAGE,
                    timestamp: generateTimestamp(),
                },
            ]);
        }
    };

    const handleAssistantChange = (newAssistant) => {
        setSelectedAssistant(newAssistant);
        // Reset conversation when switching assistants
        setConversationId('');
        // Set appropriate welcome message
        if (newAssistant.value === 'device-recovery') {
            setMessages([
                {
                    ...DEVICE_RECOVERY_WELCOME_MESSAGE,
                    timestamp: generateTimestamp(),
                },
            ]);
        } else {
            setMessages([
                {
                    ...DEFAULT_WELCOME_MESSAGE,
                    timestamp: generateTimestamp(),
                },
            ]);
        }
    };

    return (
        <>
            {/* Floating Chat Button */}
            <div className={`chatbot-toggle-container ${className || ''}`}>
                <Button variant="primary" iconName="contact" onClick={toggleChat} className="chatbot-toggle-button">
                    Help
                </Button>
            </div>

            {/* Chat Modal */}
            <Modal
                visible={isOpen}
                onDismiss={toggleChat}
                header="Device Farm Assistant"
                size="large"
                className="chatbot-modal"
            >
                <Container
                    header={
                        <Header variant="h3" description="Get help with Device Farm features and functionality">
                            Chat with Device Farm Assistant
                        </Header>
                    }
                    fitHeight
                >
                    <ChatWindow
                        messages={messages}
                        onSendMessage={handleSendMessage}
                        onNewConversation={handleNewConversation}
                        isLoading={isLoading}
                        selectedAssistant={selectedAssistant}
                        onAssistantChange={handleAssistantChange}
                    />
                </Container>
            </Modal>
        </>
    );
}
